import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Trash2,
  Eye,
  CheckCircle2,
  ShieldAlert,
  X,
  Diamond,
  Calendar,
  Lock,
  Database,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import { leeuwardenSystems } from "../data/leeuwardenSystems";
import {
  loadAssessments,
  deleteAssessment,
  loadSeedOverrides,
  loadSeedUpdates,
  loadSeedEvidence,
  loadSeedRoles,
  computeReadiness,
} from "../lib/assessmentStore";
import { vngSteps } from "../data/vngSteps";

/**
 * /register — Administrative register for the gemeente.
 * Dark-navy table styled to match the surrounding page, listing every
 * AI-system in production: 4 LWD seeds + every user-created assessment.
 * Each row mixes
 * a rich "domein / systeem" cell (domain, name, code, kader-tags,
 * laatst-getoetst date) with single-value cells for sector, risk pill,
 * silver readiness gauge, and the Open / Eye / Delete actions.
 */

// ---------- Risk taxonomy (EU AI Act language) ----------
// Colours are the app's bright accent palette (coral / gold / mint) so the
// pills keep strong contrast against the dark-navy table surface; the faint
// background tint matches each accent.
const RISK_PILLS = {
  high: {
    label: "Hoog risico",
    color: "#F08080",
    bg: "rgba(240, 128, 128, 0.14)",
  },
  medium: {
    label: "Beperkt risico",
    color: "#F4C430",
    bg: "rgba(244, 196, 48, 0.14)",
  },
  low: {
    label: "Minimaal risico",
    color: "#9FE2BF",
    bg: "rgba(159, 226, 191, 0.12)",
  },
};

// Special pill used as a dynamic *Status* indicator: once a system reaches
// 100% gereedheid the legislative risk badge is replaced with a green
// "Compliant" pill — signalling to the board that, even though the AI Act
// classifies the system as hoog/midden risico, the dossier is fully in
// order and the system is in a safe operational state.
const COMPLIANT_PILL = {
  label: "Compliant",
  color: "#9FE2BF",
  bg: "rgba(159, 226, 191, 0.16)",
};

const pillFor = (risk, readiness) =>
  readiness >= 100
    ? COMPLIANT_PILL
    : RISK_PILLS[risk] || RISK_PILLS.medium;

const RISK_FROM_LABEL = (label = "") => {
  const v = label.toLowerCase();
  if (v.includes("hoog") || v.includes("onaan")) return "high";
  if (v.includes("midden") || v.includes("medium") || v.includes("beperkt"))
    return "medium";
  return "low";
};

const FORMAT_DATE = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// Dutch month abbreviations for the compact "datum & tijd" cell
const MONTHS_NL = [
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

const FORMAT_DATETIME = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTHS_NL[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hh}:${mm}`;
};

// Demo "laatst getoetst" for the Leeuwarden seeds. Readiness is no longer
// hard-coded here — it is computed live via the shared scoring pipeline so
// the Register list always mirrors what the Cockpit dossier shows.
const LWD_META = {
  uitkeringscheck: { lastTested: "2026-01-04T09:14:00" },
  schuldhulpverlening: { lastTested: "2025-12-18T15:32:00" },
  "wmo-zorg": { lastTested: "2025-11-30T11:08:00" },
  handhaving: { lastTested: "2026-01-02T08:21:00" },
};

// Same lifecycle → green-step map as SystemResult / Framework
const LWD_LIFECYCLE_PROGRESS = {
  Initiatie: ["initiatie"],
  "Data-analyse": ["initiatie", "data-analyse"],
  Ontwerp: ["initiatie", "data-analyse", "ontwerp"],
  Ontwikkeling: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling"],
  Validatie: ["initiatie", "data-analyse", "ontwerp", "ontwikkeling", "validatie"],
  Implementatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
  ],
  Monitoring: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
  ],
  Evaluatie: [
    "initiatie",
    "data-analyse",
    "ontwerp",
    "ontwikkeling",
    "validatie",
    "implementatie",
    "monitoring",
    "evaluatie",
  ],
};

const Register = () => {
  const [assessments, setAssessments] = useState([]);
  const [seedOverrides, setSeedOverrides] = useState({});
  const [seedUpdates, setSeedUpdates] = useState({});
  const [seedEvidence, setSeedEvidence] = useState({});
  const [seedRoles, setSeedRoles] = useState({});
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setAssessments(loadAssessments());
    setSeedOverrides(loadSeedOverrides());
    setSeedUpdates(loadSeedUpdates());
    setSeedEvidence(loadSeedEvidence());
    setSeedRoles(loadSeedRoles());
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const rows = useMemo(() => {
    const vngStepIds = vngSteps.map((v) => v.id);

    const seeds = leeuwardenSystems.map((s) => {
      const baseDone = LWD_LIFECYCLE_PROGRESS[s.lifecycleStage] || [];
      const overrides = seedOverrides[s.id] || {};
      const vngStatus = {};
      vngStepIds.forEach((id) => {
        vngStatus[id] = id in overrides ? overrides[id] : baseDone.includes(id);
      });
      const risk = RISK_FROM_LABEL(s.riskClass);
      const evidence = seedEvidence[s.id] || {};
      const roles = seedRoles[s.id] || {};
      const { pct: readiness } = computeReadiness({
        vngStatus,
        evidence,
        vngStepIds,
        risk,
        roles,
      });
      const meta = LWD_META[s.id] || { lastTested: "2026-01-01" };
      const upd = seedUpdates[s.id];
      return {
        kind: "lwd",
        id: s.id,
        code: s.code,
        name: s.name,
        domain: s.tagline.split("·")[0].trim().toUpperCase(),
        sector: "Gemeente",
        risk,
        tags: ["EU AI Act Annex III", "AVG", "FRIA"],
        lastTested: upd?.lastUpdated || meta.lastTested,
        readiness,
        deletable: false,
      };
    });

    const usr = assessments.map((u) => {
      const evidence = u.evidence || {};
      const roles = u.roles || {};
      const { pct: readiness } = computeReadiness({
        vngStatus: u.vngStatus || {},
        evidence,
        vngStepIds,
        risk: u.risk,
        roles,
      });
      const tags = ["EU AI Act Annex III", "AVG"];
      if (u.safeguards && u.safeguards.includes("dpia")) tags.push("FRIA");
      return {
        kind: "user",
        id: u.id,
        code: u.code,
        name: u.name,
        domain: (u.domain || "Algemeen").toUpperCase(),
        sector: "Gemeente",
        risk: u.risk,
        tags,
        lastTested: u.lastUpdated || u.publishedAt || u.createdAt,
        readiness,
        deletable: true,
      };
    });
    return [...seeds, ...usr];
  }, [assessments, seedOverrides, seedUpdates, seedEvidence, seedRoles]);

  // Same data, sorted newest-first for the audit log section below.
  const logRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          new Date(b.lastTested).getTime() -
          new Date(a.lastTested).getTime()
      ),
    [rows]
  );

  const askDelete = (row) => {
    if (!row.deletable) return;
    setConfirmTarget({ id: row.id, name: row.name });
  };
  const cancelDelete = () => setConfirmTarget(null);
  const confirmDelete = () => {
    if (!confirmTarget) return;
    const removed = confirmTarget.name;
    deleteAssessment(confirmTarget.id);
    setAssessments(loadAssessments());
    setConfirmTarget(null);
    setToast({ tone: "success", label: `Systeem verwijderd · ${removed}` });
  };

  return (
    <div data-testid="page-register">
      {/* HEADER */}
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.55} />
        <div className="rk-container relative pt-12 md:pt-16 pb-8">
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-8"
            data-testid="register-header-row"
          >
            <div>
              <div className="overline mb-5" data-testid="register-eyebrow">
                Cockpit · Administratief register
              </div>
              <h1
                className="text-white tracking-tight leading-[1.05] max-w-4xl text-4xl md:text-6xl"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
                data-testid="register-title"
              >
                AI-systemen{" "}
                <em
                  className="italic text-[#D4D8DF]"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  in productie.
                </em>
              </h1>
              <p
                className="mt-5 text-[#9AA4B2] max-w-2xl text-sm leading-relaxed"
                data-testid="register-subtitle"
              >
                Het interne overzicht voor de gemeente: alle algoritmes onder
                beheer met hun risiconiveau, gereedheid en directe acties.
              </p>
            </div>
            <Link
              to="/assessment-choice"
              className="rk-btn-cyan shrink-0 self-start md:self-end whitespace-nowrap"
              data-testid="register-cta-new"
            >
              Nieuw AI systeem registreren
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="hairline" />

        {/* PUBLIC TRANSPARENCY BANNER */}
        <div className="rk-container py-8" data-testid="register-public-banner">
          <div
            className="border border-[#C0C5CE]/25 p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(120deg, rgba(91,192,235,0.10) 0%, rgba(91,192,235,0) 60%)",
            }}
          >
            <div className="flex items-start md:items-center gap-4">
              <span
                aria-hidden
                className="inline-block w-2 h-2 rounded-full bg-[#9FE2BF] shrink-0"
                style={{
                  boxShadow: "0 0 8px rgba(159,226,191,0.7)",
                  animation: "rk-pulse 2.4s ease-in-out infinite",
                  marginTop: "0.55rem",
                }}
              />
              <div>
                <div
                  className="overline mb-1"
                  data-testid="register-public-banner-title"
                >
                  Live · Publiek Transparantie-Dashboard
                </div>
                <div className="text-sm text-[#D4D8DF] max-w-xl">
                  Het white-label burgerdashboard met bias-metriek, FRIA en
                  menselijk toezicht — gebouwd voor inwoners, niet voor
                  beheerders.
                </div>
              </div>
            </div>
            <Link
              to="/publiek-overzicht"
              className="rk-btn-primary shrink-0"
              data-testid="register-open-public"
            >
              Open publiek overzicht
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TABLE — production overview */}
      <section
        className="rk-container py-10"
        data-testid="register-table-section"
      >
        <div
          className="overflow-x-auto"
          style={{
            background: "transparent",
            color: "#D4D8DF",
            border: "1px solid rgba(192,197,206,0.20)",
            boxShadow: "0 30px 60px -30px rgba(0,0,0,0.45)",
          }}
        >
          {/* Column headers */}
          <div
            className="hidden md:grid px-6 md:px-8 py-4 text-[10px] tracking-[0.22em] uppercase font-medium"
            style={{
              gridTemplateColumns: "3.4fr 1fr 1.4fr 1fr 1.4fr",
              color: "#94A3B8",
              background: "transparent",
              borderBottom: "1px solid rgba(192,197,206,0.08)",
            }}
            data-testid="register-table-head"
          >
            <div>Domein / Systeem</div>
            <div>Sector</div>
            <div>Risico</div>
            <div>Gereedheid</div>
            <div className="text-right">Acties</div>
          </div>

          <ul data-testid="register-table-body" className="min-w-[720px] md:min-w-0">
            {rows.map((r) => (
              <Row
                key={`${r.kind}-${r.id}`}
                row={r}
                onDelete={() => askDelete(r)}
              />
            ))}
            {rows.length === 0 && (
              <li
                className="px-6 md:px-8 py-12 text-center text-sm"
                style={{ color: "#94A3B8" }}
              >
                Nog geen assessments in het register.
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* DIVIDER + ASSESSMENT LOG */}
      <div className="rk-container">
        <div className="hairline my-6" />
      </div>

      <section
        id="log-section"
        className="rk-container pb-16"
        data-testid="register-log-section"
      >
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8"
          data-testid="register-log-header"
        >
          <div className="max-w-3xl">
            <div
              className="overline mb-3 flex items-center gap-2"
              data-testid="register-log-eyebrow"
            >
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full bg-[#9FE2BF]"
                style={{
                  boxShadow: "0 0 8px rgba(159,226,191,0.7)",
                  animation: "rk-pulse 2.4s ease-in-out infinite",
                }}
              />
              Live · uit database
            </div>
            <h2
              className="text-white tracking-tight leading-[1.1] text-3xl md:text-5xl"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              data-testid="register-log-title"
            >
              Recent uitgevoerde{" "}
              <em
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                assessments.
              </em>
            </h2>
            <p
              className="mt-4 text-sm text-[#D4D8DF] max-w-2xl leading-relaxed"
              data-testid="register-log-subtitle"
            >
              Elk zojuist voltooid assessment verschijnt hier automatisch —
              klik op een regel om het volledige rapport te openen.
            </p>
          </div>
          <a
            href="#log-section"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("log-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#7FD7E4] hover:text-white whitespace-nowrap shrink-0"
            data-testid="register-logbook-link"
          >
            Bekijk het volledige logboek
            <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
          </a>
        </div>

        <div
          className="overflow-x-auto"
          style={{
            background: "transparent",
            color: "#D4D8DF",
            border: "1px solid rgba(192,197,206,0.20)",
            boxShadow: "0 30px 60px -30px rgba(0,0,0,0.45)",
          }}
        >
          {/* internal status strip */}
          <div
            className="px-6 md:px-8 py-4 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(192,197,206,0.08)" }}
          >
            <div
              className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase"
              style={{ color: "#94A3B8" }}
            >
              <Database strokeWidth={1.25} className="w-4 h-4" />
              Logboek · {logRows.length} assessments
            </div>
            <div
              className="text-[10px] tracking-[0.18em] uppercase"
              style={{ color: "#9AA4B2" }}
            >
              Auto-update · realtime
            </div>
          </div>

          {/* column headers */}
          <div
            className="hidden md:grid px-6 md:px-8 py-3 text-[10px] tracking-[0.22em] uppercase font-medium"
            style={{
              gridTemplateColumns: "1.4fr 2.4fr 1.2fr 1fr 1fr",
              color: "#94A3B8",
              background: "transparent",
              borderBottom: "1px solid rgba(192,197,206,0.08)",
            }}
            data-testid="register-log-head"
          >
            <div>Datum &amp; tijd</div>
            <div>Organisatie</div>
            <div>Risico</div>
            <div>Gereedheid</div>
            <div className="text-right">Acties</div>
          </div>

          <ul data-testid="register-log-body" className="min-w-[720px] md:min-w-0">
            {logRows.map((r) => (
              <LogRow
                key={`log-${r.kind}-${r.id}`}
                row={r}
                onDelete={() => askDelete(r)}
              />
            ))}
            {logRows.length === 0 && (
              <li
                className="px-6 md:px-8 py-12 text-center text-sm"
                style={{ color: "#94A3B8" }}
              >
                Nog geen assessments in het logboek.
              </li>
            )}
          </ul>
        </div>
      </section>

      {confirmTarget && (
        <ConfirmDialog
          name={confirmTarget.name}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}
      {toast && <Toast label={toast.label} tone={toast.tone} />}
    </div>
  );
};

// ---------------- Row ----------------
const Row = ({ row, onDelete }) => {
  const pill = pillFor(row.risk, row.readiness);
  return (
    <li
      data-testid={`register-row-${row.id}`}
      data-kind={row.kind}
      style={{ borderBottom: "1px solid rgba(192,197,206,0.06)" }}
      className="hover:bg-white/[0.04] transition-colors duration-200"
    >
      <div
        className="grid items-center px-6 md:px-8 py-6 gap-6"
        style={{ gridTemplateColumns: "3.4fr 1fr 1.4fr 1fr 1.4fr" }}
      >
        {/* Domein / Systeem (rich cell) */}
        <Link
          to={`/platform/${row.id}`}
          className="block min-w-0"
          data-testid={`register-row-link-${row.id}`}
        >
          <div
            className="text-[10px] tracking-[0.22em] uppercase mb-1.5"
            style={{ color: "#94A3B8" }}
            data-testid={`register-domain-${row.id}`}
          >
            {row.domain}
          </div>
          <div
            className="text-2xl truncate"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 500,
              color: "#D4D8DF",
            }}
            data-testid={`register-name-${row.id}`}
          >
            {row.name}
          </div>
          <div
            className="mt-1 text-[11px] tracking-[0.18em] uppercase"
            style={{ color: "#94A3B8" }}
            data-testid={`register-code-${row.id}`}
          >
            {row.code}
          </div>
          <div
            className="mt-3 flex flex-wrap gap-2"
            data-testid={`register-tags-${row.id}`}
          >
            {row.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.18em] uppercase px-2.5 py-1"
                style={{
                  border: "1px solid rgba(192,197,206,0.18)",
                  color: "#94A3B8",
                  background: "rgba(192,197,206,0.04)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <div
            className="mt-3 flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase"
            style={{ color: "#94A3B8" }}
            data-testid={`register-tested-${row.id}`}
          >
            <Calendar strokeWidth={1.25} className="w-3.5 h-3.5" />
            Laatst getoetst {FORMAT_DATE(row.lastTested)}
          </div>
        </Link>

        {/* Sector */}
        <div
          className="text-sm"
          style={{ color: "#D4D8DF", fontFamily: "Fraunces, serif" }}
          data-testid={`register-sector-${row.id}`}
        >
          {row.sector}
        </div>

        {/* Risk pill */}
        <div>
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase"
            style={{
              border: `1px solid ${pill.color}`,
              color: pill.color,
              background: pill.bg,
              fontWeight: 500,
            }}
            data-testid={`register-risk-${row.id}`}
          >
            <Diamond
              strokeWidth={1.5}
              className="w-3 h-3"
              style={{ color: pill.color }}
            />
            {pill.label}
          </span>
        </div>

        {/* Readiness gauge — silver ring */}
        <div data-testid={`register-gauge-${row.id}`}>
          <SilverGauge value={row.readiness} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 justify-end">
          <Link
            to={`/platform/${row.id}`}
            className="rk-btn-cyan text-[11px]"
            style={{ padding: "0.5rem 1rem" }}
            data-testid={`register-action-open-${row.id}`}
          >
            Open
            <ArrowUpRight strokeWidth={1.5} className="w-3.5 h-3.5" />
          </Link>
          <Link
            to={`/platform/${row.id}`}
            aria-label="Bekijk dossier"
            data-testid={`register-action-view-${row.id}`}
            className="w-9 h-9 inline-flex items-center justify-center"
            style={{
              border: "1px solid rgba(192,197,206,0.15)",
              color: "#7FD7E4",
              background: "transparent",
            }}
          >
            <Eye strokeWidth={1.25} className="w-4 h-4" />
          </Link>
          {row.deletable ? (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Verwijder systeem"
              data-testid={`register-action-delete-${row.id}`}
              className="w-9 h-9 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(192, 57, 43, 0.3)",
                color: "#C0392B",
                background: "rgba(192, 57, 43, 0.05)",
              }}
            >
              <Trash2 strokeWidth={1.25} className="w-4 h-4" />
            </button>
          ) : (
            <span
              aria-label="Vast in handover"
              title="Vast in handover · alleen RiskaiΛ-beheerder kan dit verwijderen"
              data-testid={`register-action-locked-${row.id}`}
              className="w-9 h-9 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(192,197,206,0.12)",
                color: "rgba(192,197,206,0.3)",
              }}
            >
              <Lock strokeWidth={1.25} className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </div>
    </li>
  );
};

// ---------------- LogRow (compact audit-log row) ----------------
const LogRow = ({ row, onDelete }) => {
  const pill = pillFor(row.risk, row.readiness);
  return (
    <li
      data-testid={`register-log-row-${row.id}`}
      data-kind={row.kind}
      style={{ borderBottom: "1px solid rgba(192,197,206,0.06)" }}
      className="hover:bg-white/[0.04] transition-colors duration-200"
    >
      <Link
        to={`/platform/${row.id}`}
        className="grid items-center px-6 md:px-8 py-5 gap-4"
        style={{ gridTemplateColumns: "1.4fr 2.4fr 1.2fr 1fr 1fr" }}
        data-testid={`register-log-link-${row.id}`}
      >
        {/* Datum & tijd */}
        <div>
          <div
            className="text-sm"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 400,
              color: "#D4D8DF",
            }}
            data-testid={`register-log-date-${row.id}`}
          >
            {FORMAT_DATETIME(row.lastTested)}
          </div>
          <div
            className="mt-1 text-[10px] tracking-[0.18em] uppercase"
            style={{ color: row.kind === "lwd" ? "#94A3B8" : "#1F8A4D" }}
          >
            {row.code} · {row.kind === "lwd" ? "Live" : "Gepubliceerd"}
          </div>
        </div>

        {/* Organisatie */}
        <div className="min-w-0">
          <div
            className="text-base truncate"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 500,
              color: "#D4D8DF",
            }}
          >
            {row.name}
          </div>
          <div
            className="mt-0.5 text-[12px] truncate"
            style={{ color: "#94A3B8" }}
          >
            Gemeente Leeuwarden · Nederland · {row.tags.length} kaders
          </div>
        </div>

        {/* Risk pill */}
        <div>
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase"
            style={{
              border: `1px solid ${pill.color}`,
              color: pill.color,
              background: pill.bg,
              fontWeight: 500,
            }}
          >
            <Diamond
              strokeWidth={1.5}
              className="w-3 h-3"
              style={{ color: pill.color }}
            />
            {pill.label}
          </span>
        </div>

        {/* Compact readiness */}
        <div>
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-full text-base"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 500,
              border: "1px solid #94A3B8",
              color: "#D4D8DF",
            }}
          >
            {Math.round(row.readiness)}
          </span>
        </div>

        {/* Actions */}
        <div
          className="flex items-center gap-3 justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className="rk-btn-cyan text-[11px]"
            style={{ padding: "0.5rem 1rem" }}
          >
            Open
            <ArrowUpRight strokeWidth={1.5} className="w-3.5 h-3.5" />
          </span>
          {row.deletable ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              aria-label="Verwijder systeem"
              data-testid={`register-log-action-delete-${row.id}`}
              className="w-8 h-8 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(192, 57, 43, 0.3)",
                color: "#C0392B",
                background: "rgba(192, 57, 43, 0.05)",
              }}
            >
              <Trash2 strokeWidth={1.25} className="w-4 h-4" />
            </button>
          ) : (
            <span
              aria-label="Vast in handover"
              className="w-8 h-8 inline-flex items-center justify-center"
              style={{
                border: "1px solid rgba(192,197,206,0.12)",
                color: "rgba(192,197,206,0.3)",
              }}
            >
              <Lock strokeWidth={1.25} className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </Link>
    </li>
  );
};

// ---------------- SilverGauge (light theme) ----------------
const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const SilverGauge = ({ value = 0 }) => {
  const v = Math.max(0, Math.min(100, value));
  const cx = 50;
  const cy = 50;
  const r = 38;
  const startDeg = -130;
  const endDeg = 130;
  const sweep = endDeg - startDeg;
  const arcEnd = startDeg + (sweep * v) / 100;
  const [sx, sy] = polar(cx, cy, r, startDeg);
  const [ex, ey] = polar(cx, cy, r, endDeg);
  const [px, py] = polar(cx, cy, r, arcEnd);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  const largeArcFill = arcEnd - startDeg > 180 ? 1 : 0;
  return (
    <svg width={72} height={72} viewBox="0 0 100 100" fill="none">
      <path
        d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
        stroke="#D4D8DF"
        strokeOpacity="0.10"
        strokeWidth="1.6"
      />
      {v > 0 && (
        <path
          d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArcFill} 1 ${px} ${py}`}
          stroke="#94A3B8"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      )}
      <text
        x="50"
        y="54"
        textAnchor="middle"
        fontFamily="Fraunces, serif"
        fontSize="22"
        fontWeight="400"
        fill="#D4D8DF"
      >
        {Math.round(v)}
      </text>
      <text
        x="50"
        y="70"
        textAnchor="middle"
        fontFamily="Outfit, sans-serif"
        fontSize="6"
        letterSpacing="2"
        fill="#94A3B8"
      >
        READINESS
      </text>
    </svg>
  );
};

// ---------------- ConfirmDialog ----------------
const ConfirmDialog = ({ name, onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Bevestig verwijderen"
    data-testid="confirm-overlay"
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ background: "rgba(15, 30, 51, 0.72)", backdropFilter: "blur(6px)" }}
    onClick={onCancel}
  >
    <div
      className="relative max-w-lg w-[92%] rk-fade-up"
      style={{
        background: "#162E4A",
        border: "1px solid rgba(192,197,206,0.25)",
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        padding: "2.5rem 2rem 2rem",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-4 right-4 text-[#9AA4B2] hover:text-white"
        aria-label="Sluit"
        data-testid="confirm-close"
      >
        <X strokeWidth={1.25} className="w-5 h-5" />
      </button>
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 border border-[#F08080]/55 flex items-center justify-center shrink-0"
          aria-hidden
        >
          <ShieldAlert
            strokeWidth={1}
            className="w-6 h-6"
            style={{ color: "#F08080" }}
          />
        </div>
        <div className="min-w-0">
          <div className="overline mb-3" style={{ color: "#F08080" }}>
            Bevestig verwijdering
          </div>
          <h2
            className="text-2xl md:text-3xl text-white tracking-tight"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="confirm-title"
          >
            Weet u zeker dat u{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {name}
            </em>{" "}
            definitief wilt verwijderen?
          </h2>
          <p className="mt-4 text-sm text-[#9AA4B2] leading-relaxed">
            Deze actie verwijdert het assessment, de bijbehorende
            register-publicatie en het volledige lineage. Het kan niet ongedaan
            worden gemaakt.
          </p>
        </div>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rk-btn-ghost"
          data-testid="confirm-cancel"
          style={{
            justifyContent: "center",
            border: "1px solid rgba(192,197,206,0.4)",
            padding: "0.7rem 1.2rem",
          }}
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rk-btn-danger"
          data-testid="confirm-delete"
          autoFocus
        >
          <Trash2 strokeWidth={1.25} className="w-4 h-4" />
          Definitief verwijderen
        </button>
      </div>
    </div>
  </div>
);

// ---------------- Toast ----------------
const Toast = ({ label, tone = "success" }) => {
  const isOk = tone === "success";
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="register-toast"
      className="fixed bottom-8 right-8 z-50 rk-fade-up"
      style={{
        background: "#162E4A",
        border: `1px solid ${
          isOk ? "rgba(159,226,191,0.55)" : "rgba(240,128,128,0.55)"
        }`,
        padding: "0.85rem 1.1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
        boxShadow: "0 24px 48px -16px rgba(0,0,0,0.6)",
        maxWidth: "min(94vw, 420px)",
      }}
    >
      {isOk ? (
        <CheckCircle2
          strokeWidth={1.5}
          className="w-5 h-5"
          style={{ color: "#9FE2BF" }}
        />
      ) : (
        <ShieldAlert
          strokeWidth={1.5}
          className="w-5 h-5"
          style={{ color: "#F08080" }}
        />
      )}
      <span className="text-sm text-white">{label}</span>
    </div>
  );
};

export default Register;
